import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import {
  PartnerKycService,
  KycPartnerRow,
  KycPartnerState,
  KycStatus,
  normalizeKycStatus,
} from '../../services/partner-kyc';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';
import { IndDatePipe } from '../../pipes/ind-date';

@Component({
  selector: 'app-kyc-pending',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SelectModule,
    TableModule,
    ButtonModule,
    TagModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    IndDatePipe,
  ],
  templateUrl: './kyc-pending.html',
  styleUrls: ['./kyc-pending.css'],
})
export class KycPendingPage implements OnInit {
  private svc = inject(PartnerKycService);
  private toast = inject(ToastService);
  private router = inject(Router);
  readonly pageTitle = inject(PageTitleService).title;
  private platformId = inject(PLATFORM_ID);

  readonly stateOptions: { label: string; value: KycPartnerState | '' }[] = [
    { label: 'KYC not done (all)', value: 'NotApproved' },
    { label: 'Not submitted', value: 'NotSubmitted' },
    { label: 'Pending review', value: 'Pending' },
    { label: 'Rejected', value: 'Rejected' },
    { label: 'Approved', value: 'Approved' },
    { label: 'All partners', value: '' },
  ];

  loading = signal(false);
  search = signal('');
  state = signal<KycPartnerState | ''>('NotApproved');
  rows = signal<KycPartnerRow[]>([]);

  filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.rows();
    return this.rows().filter(
      (r) =>
        (r.partnerName || '').toLowerCase().includes(q) ||
        (r.partnerCode || '').toLowerCase().includes(q) ||
        (r.contactPerson || '').toLowerCase().includes(q) ||
        (r.phone || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q),
    );
  });

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) this.load();
  }

  load() {
    this.loading.set(true);
    const s = this.state();
    this.svc.getPartnersByKycState(s || undefined).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        const list = (Array.isArray(data) ? data : []).map((r: any) => ({
          ...r,
          kycStatus: normalizeKycStatus(r.kycStatus),
        }));
        this.rows.set(list);
      },
      error: () => this.loading.set(false),
    });
  }

  onState(v: any) {
    this.state.set(v ?? 'NotApproved');
    this.load();
  }

  /** Deep-link into the existing admin KYC review screen for this partner. */
  review(r: KycPartnerRow) {
    this.router.navigate(['/partner-kyc-review'], { queryParams: { partnerId: r.partnerId } });
  }

  /** p-tag severity for a KYC status. */
  statusSeverity(s: KycStatus): 'success' | 'danger' | 'warn' | 'secondary' {
    switch (s) {
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'danger';
      case 'Pending':
        return 'warn';
      default:
        return 'secondary';
    }
  }
}
